import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import './BadgeSheet.css';

/* What a badge stands for, on a tap — the tooltip it replaces never worked on a phone.

   The RULE is spelled out for counts ("Play 5 events"). A badge on the engagement score says only
   "earned by taking part": the score is the admin's, and its threshold would hand everybody a
   yardstick for a number they are not meant to see. */
export const badgeRule = (t, badge) => {
  const metric = badge?.metric || '';
  const count = badge?.threshold;
  if (metric === 'signal:played') return t('badgeRulePlayed', { count });
  if (metric === 'signal:captained') return t('badgeRuleCaptained', { count });
  if (metric === 'badges') return t('badgeRuleBadges', { count: (count || 0) + 1 });
  if (!metric || metric === 'manual') return t('badgeRuleManual');
  // A count of something somebody DOES may be spelled out ("Turned up: 5×") — it is their own
  // behaviour, not the score. Points (a family, the score) stay without a number.
  if (metric.startsWith('signal:')) {
    return t('badgeRuleSignal', { what: t(`signal_${metric.slice(7)}`), count });
  }
  if (metric.startsWith('family:')) {
    return t('badgeRuleFamily', { what: t(`did_${metric.slice(7)}`) });
  }
  return t('badgeRuleEngagement');
};

/* A badge's name and sentence in the reader's language: the 'badges' namespace (keyed by badge id,
   written by tools/badges/neutral_texts.py) first, the English text from the API otherwise — so a
   badge a business unit made yesterday shows its own words until somebody translates it. */
export const badgeText = (t, badge) => {
  /* Keyed by id for a one-off badge (the Herculean ladder), by `std_<code>` for a badge seeded
     from a standard set — that one has a different id in every business unit. */
  const keys = [String(badge?.id || '').toLowerCase()];
  if (badge?.code) keys.unshift(`std_${badge.code}`);
  const pick = (field, fallback) => keys.reduce(
    (found, key) => found || t(`${key}.${field}`, { ns: 'badges', defaultValue: '' }), '') || fallback;
  return {
    name: pick('name', badge?.name || ''),
    description: pick('description', badge?.description || ''),
  };
};

/* The badge's picture, or the award icon when there is none — or when the blob is gone (three of
   the legacy pictures 404: iamhercules, misterknowitall, vip). */
const BadgeImg = ({ url, className, size = '2.2rem' }) => {
  const [broken, setBroken] = useState(false);
  if (!url || broken) return <Icon name="award" theme="primary" style={{ fontSize: size }} />;
  return <img src={url} alt="" className={className} onError={() => setBroken(true)} />;
};

/* A badge tile that opens the sheet: the same pp-award look the profile already uses. */
export const BadgeTile = ({ badge, onOpen, showCount = false, muted = false, children }) => {
  const { t } = useTranslation(['common', 'badges']);
  return (
    <button type="button" className="pp-award bs-tile" onClick={() => onOpen(badge)}
            style={muted ? { opacity: 0.75 } : undefined}>
      <div className="bs-tile-wrap" style={muted ? { filter: 'grayscale(1)' } : undefined}>
        <div className="pp-award-img-wrap">
          <BadgeImg url={badge.picture_url} className="pp-award-img" />
        </div>
        {showCount && badge.count > 1 && <span className="bs-count">×{badge.count}</span>}
      </div>
      <div className="pp-award-name">{badgeText(t, badge).name}</div>
      {children}
    </button>
  );
};

const BadgeSheet = ({ badge, onClose }) => {
  const { t } = useTranslation(['common', 'badges']);

  useEffect(() => {
    if (!badge) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [badge, onClose]);

  if (!badge) return null;
  const text = badgeText(t, badge);
  return (
    <div className="bs-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="bs-sheet" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="bs-close" onClick={onClose} aria-label={t('close')}>
          <Icon name="cross" />
        </button>
        <div className="bs-art">
          <BadgeImg key={badge.id} url={badge.picture_url} size="3rem" />
        </div>
        <h3 className="bs-name">{text.name}</h3>
        <div className="bs-rule">{badgeRule(t, badge)}</div>
        {text.description && <p className="bs-desc">{text.description}</p>}
        {badge.holders?.length > 0 && (
          <>
            <div className="bs-holders-title">{t('badgeHolders')}</div>
            <div className="bs-holders">
              {badge.holders.map((h) => (
                <Link key={h.id} to={`/u/${h.id}`} className="bs-holder" onClick={onClose}>
                  {h.photo_url ? <img src={h.photo_url} alt="" /> : <Icon name="user" />}
                  <span>{h.full_name}</span>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BadgeSheet;
